import { Injectable } from '@angular/core';

export interface ToastButton {
  text?: string;
  icon?: string;
  side?: string;
  role?: string;
  handler?: () => void;
}

export interface ToastOptions {
  message: string;
  duration?: number;
  position?: 'top' | 'bottom' | 'middle';
  color?: string;
  cssClass?: string;
  buttons?: (string | ToastButton)[];
}

@Injectable({ providedIn: 'root' })
export class ToastService {

  async create(options: ToastOptions): Promise<{ present(): Promise<void> }> {
    const toast = document.createElement('div');
    toast.className = 'toast-notification';

    if (options.cssClass) {
      options.cssClass.split(' ').forEach(c => toast.classList.add(c));
    }

    // Color mapping
    const colorMap: Record<string, string> = {
      primary: '#1264a3',
      danger: '#e01e5a',
      success: '#1ab55a',
      warning: '#f0b429',
    };
    const bgColor = colorMap[options.color || 'primary'] || colorMap.primary;
    toast.style.setProperty('--toast-bg', bgColor);

    // Position
    const position = options.position || 'bottom';
    toast.classList.add(`toast-${position}`);

    // Message
    const msgEl = document.createElement('span');
    msgEl.className = 'toast-message';
    msgEl.textContent = options.message;
    toast.appendChild(msgEl);

    // Buttons
    if (options.buttons?.length) {
      const btnWrapper = document.createElement('div');
      btnWrapper.className = 'toast-buttons';
      options.buttons.forEach(btn => {
        const buttonEl = document.createElement('button');
        buttonEl.className = 'toast-btn';
        if (typeof btn === 'string') {
          buttonEl.textContent = btn;
          buttonEl.addEventListener('click', () => dismiss());
        } else {
          buttonEl.textContent = btn.text;
          buttonEl.addEventListener('click', () => {
            if (btn.handler) {
              btn.handler();
            }
            dismiss();
          });
        }
        btnWrapper.appendChild(buttonEl);
      });
      toast.appendChild(btnWrapper);
    }

    const dismiss = () => {
      toast.classList.remove('toast-visible');
      setTimeout(() => toast.remove(), 300);
    };

    return {
      present: async () => {
        document.body.appendChild(toast);
        requestAnimationFrame(() => toast.classList.add('toast-visible'));

        const duration = options.duration ?? 2000;
        if (duration > 0) {
          setTimeout(() => dismiss(), duration);
        }
      },
    };
  }
}
